/**
 * Superhost helpers.
 *
 * Superhost status is decided by deepPool and stored in Postgres (`superhosts`),
 * not in Sharetribe: the server puts the current holders into the preloaded
 * state (`src/ducks/superhosts.duck.js`) and `configureStore` hands them to
 * `setSuperhostRegistry`. Every check below reads that registry and nothing else:
 * since phase 4 of docs/superhost.md Sharetribe's `publicData.superhost` is
 * neither written nor read.
 *
 * Fields:
 *   - active:       whether the user currently holds the superhost badge.
 *   - valid_until:  last day of the badge ('YYYY-MM-DD', a Madrid day, valid all
 *                   of it) or an ISO timestamp in older data.
 *
 * The dashboard progress (`{ criteria, metrics }`) comes from
 * `GET /api/superhost/me` only: since phase 3 deepPool no longer writes
 * `privateData.superhost`.
 */

// 1-indexed positions on the search results grid where superhosts are surfaced so
// they stand out. The list is illustrative/strategic and can be tuned freely; any
// position beyond the number of results on the page is ignored.
export const SUPERHOST_STRATEGIC_POSITIONS = [2, 4, 5, 9, 13, 17, 20];

let registry = {};

/**
 * Replace the current superhost holders (`{ [userId]: { active, valid_until } }`).
 * Called once by `configureStore` with the preloaded list.
 */
export const setSuperhostRegistry = byId => {
  registry = byId || {};
};

/**
 * The `superhost` object of a user entity (denormalised or raw SDK resource), from
 * the registry. A user who is not in it does not hold the badge.
 */
export const getSuperhostData = user => {
  const id = user?.id?.uuid;
  return (id && registry[id]) || null;
};

/**
 * Date (ISO string) until the current superhost badge remains valid.
 * Read from the public `superhost` object: prefer `valid_until`, with camelCase /
 * legacy aliases as fallbacks. Returns `null` when none is present.
 */
export const getSuperhostValidUntil = user => {
  const superhost = getSuperhostData(user);
  if (!superhost) {
    return null;
  }
  return (
    superhost.valid_until ||
    superhost.validUntil ||
    superhost.expires_at ||
    superhost.expiresAt ||
    null
  );
};

/**
 * Descriptors for the criteria shown on the host dashboard, in display order.
 *
 * Each descriptor maps a key shared by `criteria` (the target) and `metrics`
 * (the host's value) to:
 *   - labelId:       translation id for the row label.
 *   - infoId:        translation id for the criterion explanation (shown in the
 *                    per-criterion info modal on the dashboard).
 *   - format:        how the numbers render ('count' | 'rating' | 'percent').
 *   - lowerIsBetter: when true the host passes by staying at/under the target
 *                    (e.g. cancellations); otherwise they pass by meeting/exceeding it.
 */
export const SUPERHOST_CRITERIA_ROWS = [
  {
    id: 'reservations_total',
    labelId: 'DashboardPage.rowTotalReservas',
    infoId: 'DashboardPage.infoTotalReservas',
    format: 'count',
  },
  {
    id: 'completed',
    labelId: 'DashboardPage.rowCompleted',
    infoId: 'DashboardPage.infoCompleted',
    format: 'count',
  },
  {
    id: 'guest_review_rate',
    labelId: 'DashboardPage.rowGuestReviewRate',
    infoId: 'DashboardPage.infoGuestReviewRate',
    format: 'percent',
  },
  {
    id: 'reviews',
    labelId: 'DashboardPage.rowReviews',
    infoId: 'DashboardPage.infoReviews',
    format: 'count',
  },
  {
    id: 'rating',
    labelId: 'DashboardPage.rowRating',
    infoId: 'DashboardPage.infoRating',
    format: 'rating',
  },
  {
    id: 'response_rate',
    labelId: 'DashboardPage.rowResponseRate',
    infoId: 'DashboardPage.infoResponseRate',
    format: 'percent',
  },
  {
    id: 'decline_rate',
    labelId: 'DashboardPage.rowDeclineRate',
    infoId: 'DashboardPage.infoDeclineRate',
    format: 'percent',
    lowerIsBetter: true,
  },
  {
    id: 'provider_cancel_rate',
    labelId: 'DashboardPage.rowCancelRate',
    infoId: 'DashboardPage.infoCancelRate',
    format: 'percent',
    lowerIsBetter: true,
  },
];

/**
 * Build the dashboard rows from a host's superhost progress, as fetched from
 * `/api/superhost/me`: `{ criteria, metrics }`, where `criteria` are the target
 * thresholds and `metrics` the host's current values for the same keys.
 *
 * Returns one entry per descriptor in `SUPERHOST_CRITERIA_ROWS`:
 *   { id, labelId, format, lowerIsBetter, target, value, passed }
 * A row only counts as passed when both numbers are present and the comparison
 * (>= target, or <= target when lowerIsBetter) holds — a missing value never passes.
 * Returns an empty array when there is no progress at all.
 */
export const buildSuperhostCriteriaRows = progress => {
  if (!progress) {
    return [];
  }
  const criteria = progress.criteria || {};
  const metrics = progress.metrics || {};

  return SUPERHOST_CRITERIA_ROWS.map(descriptor => {
    const target = criteria[descriptor.id];
    const value = metrics[descriptor.id];
    const lowerIsBetter = descriptor.lowerIsBetter === true;
    const hasNumbers = Number.isFinite(target) && Number.isFinite(value);
    const passed = hasNumbers && (lowerIsBetter ? value <= target : value >= target);

    return {
      id: descriptor.id,
      labelId: descriptor.labelId,
      infoId: descriptor.infoId,
      format: descriptor.format,
      lowerIsBetter,
      target: Number.isFinite(target) ? target : null,
      value: Number.isFinite(value) ? value : null,
      passed,
    };
  });
};

/**
 * Render a criterion value according to its format ('count' | 'rating' | 'percent').
 * A missing value (null/undefined) shows an em dash. Needs a react-intl `intl`
 * instance for locale-aware number formatting.
 */
export const formatCriterionValue = (value, format, intl) => {
  if (value == null) {
    return '—';
  }
  if (format === 'percent') {
    return `${intl.formatNumber(value, { maximumFractionDigits: 1 })}%`;
  }
  if (format === 'rating') {
    return intl.formatNumber(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }
  return intl.formatNumber(value);
};

/**
 * Format a Superhost target threshold. For "lower is better" criteria (decline /
 * cancel rates) the target is prefixed with "menos de".
 */
export const formatCriterionTarget = (value, format, intl, lowerIsBetter) => {
  const formatted = formatCriterionValue(value, format, intl);
  if (formatted === '—' || !lowerIsBetter) {
    return formatted;
  }
  return intl.formatMessage({ id: 'DashboardPage.criterionLessThan' }, { value: formatted });
};

/**
 * Number of dashboard rows the host has not yet met.
 */
export const countUnmetSuperhostCriteria = rows =>
  (rows || []).reduce((acc, row) => (row.passed ? acc : acc + 1), 0);

const DAY_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const madridToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid' }).format(new Date());

/**
 * Whether a user currently holds the superhost badge: `active` is true and
 * `valid_until` (or a legacy expiry field), when present, has not passed. A bare
 * 'YYYY-MM-DD' is a Madrid day and keeps the badge for all of it. Same rule as
 * `holdsSuperhostBadge` in server/api-util/superhost.js.
 */
export const isSuperhostActive = user => {
  const superhost = getSuperhostData(user);
  if (!superhost || superhost.active !== true) {
    return false;
  }
  const validUntil = getSuperhostValidUntil(user);
  if (validUntil) {
    if (DAY_ONLY.test(validUntil)) {
      return validUntil >= madridToday();
    }
    const expiry = new Date(validUntil).getTime();
    // A present-but-unparseable date is treated as "no expiry" rather than expired,
    // so a malformed value never silently revokes the badge.
    if (Number.isFinite(expiry) && expiry < Date.now()) {
      return false;
    }
  }
  return true;
};

/**
 * Active superhost who no longer meets every criterion. The badge stays until the
 * next review (`valid_until`), but the host should be warned to recover.
 */
export const isSuperhostAtRisk = (user, rows) =>
  isSuperhostActive(user) && countUnmetSuperhostCriteria(rows) > 0;

/**
 * Whether a listing belongs to an active superhost.
 */
export const isSuperhostListing = listing => isSuperhostActive(listing?.author);

/**
 * Predicate for raw SDK listings whose `author` is only a relationship reference.
 * Resolves the author from the response `included` users array.
 */
export const makeRawSuperhostPredicate = included => {
  const usersById = {};
  (included || []).forEach(r => {
    if (r?.type === 'user' && r?.id?.uuid) {
      usersById[r.id.uuid] = r;
    }
  });
  return rawListing => {
    const authorId = rawListing?.relationships?.author?.data?.id?.uuid;
    return authorId ? isSuperhostActive(usersById[authorId]) : false;
  };
};

/** Fisher–Yates shuffle (mutates a copy). */
const shuffleArray = array => {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

/**
 * Reorder a page of listings so that listings owned by active superhosts land on
 * the configured strategic positions, making them stand out on the grid.
 *
 * - Superhosts assigned to strategic slots are picked in random order each run.
 * - A superhost is only moved when the strategic slot is strictly higher on the
 *   page than their original position; otherwise they stay in the organic order
 *   and the next superhost is considered for that slot.
 * - The relative order of non-superhost listings is preserved.
 * - Superhosts that don't fit in a strategic slot keep their original order in the
 *   remaining slots (they are not dropped).
 * - The returned array contains exactly the same listings as the input.
 *
 * `isSuperhost` lets callers override how a listing is classified. The default
 * works on denormalised listings (`listing.author` is the resolved user); raw SDK
 * responses, whose `author` is only a relationship reference, must pass a predicate
 * that resolves the author from the response's `included` users.
 */
export const reorderListingsForSuperhosts = (
  listings,
  positions = SUPERHOST_STRATEGIC_POSITIONS,
  isSuperhost = isSuperhostListing
) => {
  if (!Array.isArray(listings) || listings.length === 0) {
    return listings;
  }

  const superhosts = listings.filter(isSuperhost);
  if (superhosts.length === 0) {
    return listings;
  }

  const originalIndexById = new Map();
  listings.forEach((listing, index) => {
    const id = listingUuid(listing);
    if (id) {
      originalIndexById.set(id, index);
    }
  });

  const slots = new Array(listings.length).fill(null);

  // 0-indexed, de-duplicated, in ascending order, within bounds.
  const targetIndexes = Array.from(new Set(positions))
    .map(p => p - 1)
    .filter(i => i >= 0 && i < listings.length)
    .sort((a, b) => a - b);

  const superQueue = shuffleArray(superhosts);
  const placedIds = new Set();

  for (const targetIndex of targetIndexes) {
    for (let q = 0; q < superQueue.length; q++) {
      const candidate = superQueue[q];
      const id = listingUuid(candidate);
      const originalIndex = id != null ? originalIndexById.get(id) : undefined;

      if (originalIndex === undefined) {
        continue;
      }

      // Lower index = higher on the page. Skip when the slot would not improve rank.
      if (targetIndex >= originalIndex) {
        continue;
      }

      slots[targetIndex] = candidate;
      if (id) {
        placedIds.add(id);
      }
      superQueue.splice(q, 1);
      break;
    }
  }

  // Fill the remaining slots with everything not yet placed (leftover superhosts +
  // all regular listings), preserving their original relative order.
  const remaining = listings.filter(l => {
    const id = listingUuid(l);
    return id ? !placedIds.has(id) : true;
  });
  let r = 0;
  for (let i = 0; i < slots.length; i++) {
    if (slots[i] === null) {
      slots[i] = remaining[r++];
    }
  }

  return slots;
};

const listingUuid = listing => listing?.id?.uuid;

/**
 * Reorder a full result set so superhosts fill the strategic positions of each page,
 * cascading page by page: page-1 slots first, then page-2 slots, and so on.
 *
 * Listings are never dropped — when a superhost takes a slot on page 1, regular
 * listings shift down and any overflow beyond the 24th position continues naturally
 * on page 2 (and so on).
 */
export const reorderListingsForSuperhostsAcrossPages = (
  listings,
  pageSize = 24,
  positions = SUPERHOST_STRATEGIC_POSITIONS,
  isSuperhost = isSuperhostListing
) => {
  if (!Array.isArray(listings) || listings.length === 0 || !pageSize || pageSize < 1) {
    return listings;
  }

  if (!listings.some(isSuperhost)) {
    return listings;
  }

  const totalPages = Math.ceil(listings.length / pageSize);
  const globalPositions = [];
  for (let page = 0; page < totalPages; page++) {
    for (const pos of positions) {
      globalPositions.push(page * pageSize + pos);
    }
  }

  return reorderListingsForSuperhosts(listings, globalPositions, isSuperhost);
};
